import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ShippingPolicy = () => {
  const policySections = [
    {
      number: "01",
      title: "Order Processing Time",
      content: "All orders are processed within 2 to 4 hours. Orders are shipped on weekends and holidays also. If we anticipate a significant delay in shipping your order, we will notify you via email."
    },
    {
      number: "02",
      title: "Shipping Methods",
      content: "We process and ship all confirmed orders within 24 hours. Please note that delivery times may vary based on your location and the shipping method selected."
    },
    {
      number: "03",
      title: "Shipping Rates",
      content: "Shipping costs are calculated at checkout based on the weight of your order and your location. We aim to provide competitive rates and may offer promotions or free shipping for orders over a certain amount."
    },
    {
      number: "04",
      title: "Delivery Locations",
      content: "We currently ship within 5km radius in Hyderabad. Unfortunately, we do not ship to P.O. Boxes or international addresses at this time."
    },
    {
      number: "05",
      title: "Order Tracking",
      content: "Once your order has shipped, you will receive a confirmation email with a tracking number. You can use this number to track your package on the carrier's website."
    },
    {
      number: "06",
      title: "Damaged or Incorrect Orders",
      content: "If your order arrives damaged or you receive the wrong items, please contact us within 4 hours of delivery. We will work with you to resolve the issue promptly."
    },
    {
      number: "07",
      title: "Freshness Guarantee",
      content: "To ensure the quality and freshness of our baked goods, we recommend placing your order for delivery as close to your intended consumption date as possible. Our products are made with natural ingredients and are best enjoyed fresh."
    },
    {
      number: "08",
      title: "Changes to Shipping Policy",
      content: "Bakelore reserves the right to update this Shipping Policy at any time. We will notify you of any significant changes by posting a notice on our website. Your continued use of our services constitutes your acceptance of the new policy."
    }
  ];

  return (
    <>
<Helmet>
        <title>Shipping Policy | Bakelore</title>
        <meta name="description" content="Bakelore Shipping Policy - Learn about our shipping procedures, delivery times, and policies." />
        <meta name="keywords" content="Bakelore, Shipping Policy, Delivery, Order Tracking" />
        <meta property="og:title" content="Shipping Policy | Bakelore" />
        <meta property="og:description" content="Learn about our shipping procedures, delivery times, and policies." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Shipping Policy | Bakelore" />
        <meta name="twitter:description" content="Learn about our shipping procedures, delivery times, and policies." />
      </Helmet>

      {/* Hero Banner */}
      <section className="relative h-[50vh] md:h-[60vh] overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1920&q=80')",
          }}
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#C08B89]/90 via-[#C08B89]/70 to-[#C08B89]/90" />
        
        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-white/10 rounded-full blur-xl" />
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-white/10 rounded-full blur-xl" />
        
        {/* Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
          <div className="inline-block mb-4 px-4 py-1 bg-white/20 rounded-full backdrop-blur-sm">
            <span className="text-white text-sm font-medium tracking-wider uppercase">
              Delivery Information
            </span>
          </div>
          <h1 
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 tracking-tight"
            style={{ fontFamily: "Philosopher, serif" }}
          >
            Shipping Policy
          </h1>
          <p 
            className="text-white/90 text-lg md:text-xl max-w-2xl mb-8"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Fresh & On-Time Delivery
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/contact">
              <button className="px-8 py-3 bg-white text-[#C08B89] font-semibold rounded-lg hover:bg-white/90 transition-all duration-300 shadow-lg">
                Contact Us
              </button>
            </Link>
            <Link to="/">
              <button className="px-8 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-300">
                Back to Home
              </button>
            </Link>
          </div>
        </div>
        
        {/* Bottom Wave */}
        <div className="absolute bottom-0 left-0 right-0">
<svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#faf8f5"/>
          </svg>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-12 md:py-16 bg-[#faf8f5]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 border border-gray-100">
            <div className="flex items-center6">
               justify-center mb-<div className="w-16 h-16 bg-[#C08B89]/10 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-[#C08B89]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
            </div>
            <p 
              className="text-gray-700 text-lg md:text-xl text-center leading-relaxed"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              At <span className="font-bold text-[#C08B89]">Vivasvan Foods and Beverages Private Limited</span>, we strive to ensure that your delicious treats arrive fresh and on time. Please read our shipping policy carefully to understand our procedures and options.
            </p>
          </div>
        </div>
      </section>

      {/* Policy Sections */}
      <section className="py-12 md:py-16 bg-[#faf8f5]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid gap-6">
            {policySections.map((section, index) => (
              <div 
                key={index}
                className="group bg-white rounded-xl p-6 md:p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#C08B89]/30"
              >
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-[#C08B89] to-[#d4a5a3] text-white font-bold text-lg rounded-lg">
                      {section.number}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h2 
                      className="text-xl md:text-2xl font-bold text-gray-800 mb-3 group-hover:text-[#C08B89] transition-colors"
                      style={{ fontFamily: "Philosopher, serif" }}
                    >
                      {section.title}
                    </h2>
                    <p 
                      className="text-gray-600 leading-relaxed"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      {section.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-br from-[#C08B89] to-[#d4a5a3] rounded-2xl shadow-xl p-8 md:p-12 text-white">
            <div className="text-center mb-8">
              <h2 
                className="kint text-3xl md:text-4xl font-bold mb-4"
                style={{ fontFamily: "Philosopher, serif" }}
              >
                Have Questions?
              </h2>
              <p className="text-white/90 text-lg" style={{ fontFamily: "Manrope, sans-serif" }}>
                If you have any questions or concerns regarding our shipping policy, please contact us.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="kint font-semibold mb-2">Address</h3>
                <p className="text-white/80 text-sm">
                  1-103/5/1 Hitec City, Main Road, Madhapur, Hyderabad, Telangana, 500081.
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="kint font-semibold mb-2">Email</h3>
                <a href="mailto:hello@bakelore.com" className="text-white/80 hover:text-white hover:underline">
                  hello@bakelore.com
                </a>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <h3 className="kint font-semibold mb-2">Call/WhatsApp</h3>
                <div className="flex flex-col gap-1">
                  <a href="tel:+918121034991" className="text-white/80 hover:text-white hover:underline text-sm">+91 8121034991</a>
                  <a href="tel:+918121034992" className="text-white/80 hover:text-white hover:underline text-sm">+91 8121034992</a>
                  <a href="tel:+918121034993" className="text-white/80 hover:text-white hover:underline text-sm">+91 8121034993</a>
                </div>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <Link to="/contact">
                <button className="px-8 py-3 bg-white text-[#C08B89] font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg">
                  Contact Us
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Thank You Section */}
      <section className="py-12 bg-[#faf8f5]">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <p 
            className="text-gray-700 text-xl"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Thank you for choosing <span className="font-bold text-[#C08B89]">Bakelore</span>! We appreciate your support and look forward to delivering your favorite treats.
          </p>
        </div>
      </section>
    </>
  );
};

export default ShippingPolicy;

